import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useBlocker } from 'react-router-dom';
import { useSocket } from '../contexts/socketContext';
import { useAuth } from '../contexts/authContext';
import './ChatPage.css';
import { API_URL } from '../lib/config';
import AddFriendManager from '../components/AddFriendManager';
// import User from '../../../server/models/User';

function ChatPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const socket = useSocket();
    const { currentUser } = useAuth();

    // location.state에서 초기 정보 가져오기 (roomId는 필수, 나머지는 없을 수 있음)
    const { chatRoomId, partner: initialPartner, isReadOnly: initialIsReadOnly, from } = location.state || {};

    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    // const [isTyping, setIsTyping] = useState(false); // [추가] 현재 타자 중인지 상태
    const typingTimeoutRef = useRef(null);           // [추가] 타자 멈춤 감지용 타이머
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const isTypingRef = useRef(false);  // ▼▼▼ [신규 추가] 즉각적인 상태 추적용 ref

    // ▼▼▼ [신규 추가] 1. 방 번호별 고유한 임시저장 키 생성 ▼▼▼
    const draftKey = `chat_draft_${chatRoomId}`;

    // ▼▼▼ [신규 추가] 타자 입력 감지 공통 핸들러 ▼▼▼
    const handleTypingActivity = () => {
        if (!socket || roomInfo?.type !== 'AI_FRIEND') return;

        // 최초 1회만 '타자 시작' 전송
        if (!isTypingRef.current) {
            isTypingRef.current = true;
            // setIsTyping(true);
            socket.emit('typing', { chatRoomId });
        }

        // 기존 2초 대기 타이머 리셋
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

        // 2초 동안 추가 입력이 없으면 '타자 멈춤' 판정
        typingTimeoutRef.current = setTimeout(() => {
            isTypingRef.current = false;
            // setIsTyping(false);
            socket.emit('stopped_typing', { chatRoomId });
        }, 2000);
    };

    // ▼▼▼ [신규 추가] 2. 방에 들어올 때 임시저장된 글이 있으면 불러오기 ▼▼▼
    useEffect(() => {
        if (chatRoomId) {
            const savedDraft = localStorage.getItem(draftKey);
            if (savedDraft) {
                setInputMessage(savedDraft);
            }
        }
    }, [chatRoomId, draftKey]);

    // ▼▼▼ [신규 추가] 3. 입력값이 바뀔 때마다 실시간으로 로컬 스토리지에 저장 ▼▼▼
    useEffect(() => {
        if (chatRoomId) {
            if (inputMessage.trim() !== '') {
                localStorage.setItem(draftKey, inputMessage);
            } else {
                localStorage.removeItem(draftKey); // 글을 싹 지웠으면 스토리지에서도 삭제
            }
        }
    }, [inputMessage, chatRoomId, draftKey]);

    // 채팅방 상세 정보와 로딩 상태 추가
    const [roomInfo, setRoomInfo] = useState(null);
    const [partnerInfo, setPartnerInfo] = useState(initialPartner); // API 호출 전 임시 사용

    const roomInfoRef = useRef(roomInfo);
    const partnerInfoRef = useRef(partnerInfo);

    useEffect(() => {
        roomInfoRef.current = roomInfo;
    }, [roomInfo]);

    useEffect(() => {
        partnerInfoRef.current = partnerInfo;
    }, [partnerInfo]);

    const [loadingRoomInfo, setLoadingRoomInfo] = useState(true);
    // const [isReadOnlyMode, setIsReadOnlyMode] = useState(initialIsReadOnly || false); // 읽기 전용 모드 상태
    const isReadOnlyMode = initialIsReadOnly || false;

    const [showFullCharacter, setShowFullCharacter] = useState(false);
    const [openMenuMessageId, setOpenMenuMessageId] = useState(null);

    const currentUserNickname = localStorage.getItem('userNickname') || '나';
    const currentUserId = localStorage.getItem('currentUserId'); // MongoDB ID

    const [partnerUnreadCount, setPartnerUnreadCount] = useState(0);

    // useBlocker 조건: 활성화된 REALTIME_MATCH일 때만 작동 (읽기 전용 모드 제외)
    const blocker = useBlocker(() => roomInfo?.type === 'REALTIME_MATCH' && roomInfo?.isActive && !isReadOnlyMode);

    // ▼▼▼ 소켓 핸들러들을 useCallback으로 정의 ▼▼▼
    const handleNewMessage = useCallback((newMessage) => {
        if (newMessage.chatRoom === chatRoomId) {
           setMessages(prevMessages => [...prevMessages, newMessage]);
           // [추가] 내가 보낸 메시지면, 상대방 안 읽은 개수 +1
           if (newMessage.sender === currentUserId) {
               const incrementAmount = (newMessage.unreadIncrement !== undefined) 
                                        ? newMessage.unreadIncrement 
                                        : 1;
               
               setPartnerUnreadCount(prev => prev + incrementAmount);
           }

           const currentRoomInfo = roomInfoRef.current;
           const currentPartnerInfo = partnerInfoRef.current;
           // 이제 roomInfo와 partnerInfo의 최신 값을 안전하게 참조 가능
           if (currentRoomInfo?.type === 'RECOMMEND_MATCH' && !currentRoomInfo.isAccepted && currentRoomInfo.initiator !== currentUserId && newMessage.sender === currentPartnerInfo?.userId) {
                setRoomInfo(prev => ({ ...prev, isAccepted: true }));
                // console.log("상대방 메시지 수신으로 isAccepted=true 변경됨");
           }
        }
   }, [chatRoomId, currentUserId]); // 이 함수가 의존하는 값들 명시

   const handlePartnerRead = useCallback(({readerId}) => {
        if (readerId === currentUserId) return;
        setPartnerUnreadCount(0); // 0으로 초기화
   }, [currentUserId]);

   const handleInvitationAccepted = useCallback(({ roomId }) => {
        if (roomId === chatRoomId && roomInfo?.initiator === currentUserId) {
             setRoomInfo(prev => ({ ...prev, isAccepted: true }));
             console.log("상대방 수락 이벤트 수신으로 isAccepted=true 변경됨");
        }
   }, [chatRoomId, roomInfo, currentUserId]);

   const handleChatEnd = useCallback((data) => {
        if(data.chatRoomId === chatRoomId && roomInfo) { // roomInfo 존재 확인
            if (roomInfo.type === 'REALTIME_MATCH'){
                alert(`${partnerInfo?.nickname || '상대방'}님이 대화를 종료했습니다.`);
                setRoomInfo(prev => ({ ...prev, isActive: false }));
            }
        }
   }, [chatRoomId, roomInfo, partnerInfo]);

   const handleReportFeedback = useCallback(({ message }) => { alert(message); }, []);

   // ▼▼▼▼▼▼▼▼▼▼ [여기에 새로 추가!] 에러 피드백 수신 핸들러 ▼▼▼▼▼▼▼▼▼▼
   const handleErrorFeedback = useCallback((data) => {
        if (data.type === 'AI_LIMIT_EXCEEDED') {
            alert(data.message);
            // 🚨 핵심: 여기서 setInputMessage('')를 하지 않기 때문에 
            // 유저가 쓰던 글이 사라지지 않고 그대로 남습니다! (임시저장 효과)
        } else {
            alert(data.message || '오류가 발생했습니다.');
        }
   }, []);
   // ▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲

   const getUnreadMessageIds = () => {
        if (partnerUnreadCount <= 0) {
            return new Set();
        }
        const myMessages = messages.filter(m => m.sender === currentUserId);
        const unreadMessages = myMessages.slice(-partnerUnreadCount); // 뒤에서부터 N개
        return new Set(unreadMessages.map(m => m._id));
    };

    const unreadIds = getUnreadMessageIds(); // 렌더링 할 때마다 계산

    // 화면 높이 계산 useEffect (변경 없음)
    useEffect(() => {
        const setVh = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };
        setVh();
        window.addEventListener('resize', setVh);
        return () => window.removeEventListener('resize', setVh);
    }, []);

    // useBlocker 처리 useEffect (roomInfo 의존성 추가)
    useEffect(() => {
        if (blocker.state === 'blocked') {
            if (window.confirm('대화를 중단하고 정말로 채팅방을 나가시겠습니까?')) {
                // REALTIME_MATCH 일 때만 서버에 알림 (백엔드 로직에 따라 조정 가능)
                if (socket && roomInfo?.type === 'REALTIME_MATCH') {
                    socket.emit('leave_chat_room', { chatRoomId });
                }
                blocker.proceed(); // 페이지 이동 허용
            } else {
                blocker.reset(); // 페이지 이동 취소
            }
        }
    }, [blocker, socket, chatRoomId, roomInfo]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // 메뉴가 열려있을 때만 실행
            if (openMenuMessageId) {
                // 클릭한 요소가 '.message-menu-container' (점세개 버튼 + 팝업) 안에 없으면 닫기
                if (!event.target.closest('.message-menu-container')) {
                    setOpenMenuMessageId(null);
                }
            }
        };

        // 화면 전체에 클릭 이벤트 리스너 등록
        document.addEventListener('mousedown', handleClickOutside);
        
        // 컴포넌트가 사라지거나 업데이트될 때 리스너 정리
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuMessageId]);

    // 메인 로직 useEffect: 채팅방 정보 로드, 메시지 로드, 소켓 리스너 설정
    useEffect(() => {
        // 필수 정보 없으면 로직 중단
        if (!chatRoomId || !currentUser) {
            alert('채팅방 정보나 사용자 정보가 올바르지 않습니다.');
            navigate(from || '/');
            return;
        }

        let isMounted = true; // 언마운트 시 비동기 작업 중단 플래그

        // 1. 채팅방 상세 정보 및 기록 불러오기 함수 정의
        const fetchRoomInfoAndHistory = async () => {
            setLoadingRoomInfo(true);
            try {
                const idToken = await currentUser.getIdToken();
                const roomResponse = await fetch(`${API_URL}/api/chat/chat-room/${chatRoomId}`, { headers: { 'Authorization': `Bearer ${idToken}` } });
                if (!roomResponse.ok) throw new Error('채팅방 정보를 불러오지 못했습니다.');
                const fetchedRoomInfo = await roomResponse.json();
                if (!isMounted) return;
                setRoomInfo(fetchedRoomInfo);
                if (fetchedRoomInfo.partnerUnreadCount !== undefined){
                    setPartnerUnreadCount(fetchedRoomInfo.partnerUnreadCount);
                }
                if (fetchedRoomInfo.partner) setPartnerInfo(fetchedRoomInfo.partner);

                // const shouldFetchHistory = fetchedRoomInfo.type === 'RECOMMEND_MATCH' || isReadOnlyMode;
                const shouldFetchHistory = 
                        fetchedRoomInfo.type === 'RECOMMEND_MATCH' || 
                        fetchedRoomInfo.type === 'FRIEND' ||
                        fetchedRoomInfo.type === 'AI_FRIEND' ||
                        isReadOnlyMode;

                if (shouldFetchHistory) {
                    const historyApiUrl = `${API_URL}/api/chat/chat-history/${chatRoomId}`;
                    const historyResponse = await fetch(historyApiUrl, { headers: { 'Authorization': `Bearer ${idToken}` } });
                    if (!historyResponse.ok) throw new Error('채팅 기록을 불러오는 데 실패했습니다.');
                    const historyData = await historyResponse.json();
                    const historyMessages = historyData.messages;
                    if (!isMounted) return;
                    setMessages(historyMessages || []);
                }

                // API 호출이 성공하고, roomInfo가 설정된 직후에 실행
                if (isMounted && !isReadOnlyMode) {
                    // [수정] state인 roomInfo 대신, 방금 가져온 fetchedRoomInfo를 사용해야 함
                    if (fetchedRoomInfo.type === 'RECOMMEND_MATCH' && !fetchedRoomInfo.isAccepted && fetchedRoomInfo.initiator !== currentUserId) {
                        // console.log("초대 수락 전이므로 '읽음' 처리하지 않음.");
                    } else {
                        // 그 외 모든 경우 (실시간, 내가 보낸 초대, 이미 수락한 대화)
                        socket.emit('mark_as_read', { chatRoomId, userId: currentUserId });
                        // console.log("★★ [Socket] Emitting mark_as_read ★★"); // 로그 확인용
                    }
                }

                if (isMounted && socket && !isReadOnlyMode) {
                    // 서버에 'join_room' 이벤트를 보내 현재 소켓이 이 방에 참여함을 알림
                    socket.emit('join_room', { chatRoomId,
                        userId : currentUserId
                     });
                    // console.log(`[Socket] Emitting join_room for: ${chatRoomId}`);
                }

            } catch (error) {
                console.error(error);
                if (isMounted) { alert(error.message); navigate(from || (isReadOnlyMode ? -1 : '/')); }
            } finally {
                if (isMounted) setLoadingRoomInfo(false);
            }
        };

        // 함수 실행
        fetchRoomInfoAndHistory();

        return () => {
            isMounted = false;
            // ▼▼▼ [신규 추가] 화면이 꺼질 때 무조건 방을 나갔다고 서버에 쏴줌 ▼▼▼
            if (socket && !isReadOnlyMode) {
                socket.emit('leave_chat_room', { chatRoomId });
            }
        };
    }, [chatRoomId, currentUser, socket, navigate, from, isReadOnlyMode, initialPartner, currentUserId]);

    // ▼▼▼ 소켓 리스너 등록/해제 전용 useEffect 추가 ▼▼▼
    useEffect(() => {
        // 소켓이 없거나 읽기 전용 모드면 리스너 등록 안 함
        if (!socket || isReadOnlyMode) return;

        // useCallback으로 만든 핸들러들을 등록
        socket.on('new_message', handleNewMessage);
        socket.on('partner_read_messages', handlePartnerRead);
        socket.on('invitation_accepted', handleInvitationAccepted);
        socket.on('partner_disconnected', handleChatEnd);
        socket.on('partner_left_chat_room', handleChatEnd);
        socket.on('report_feedback', handleReportFeedback);
        socket.on('error_feedback', handleErrorFeedback);

        // Cleanup 함수: 컴포넌트 언마운트 시 또는 socket이 바뀔 때 리스너 해제
        return () => {
            socket.off('new_message', handleNewMessage);
            socket.off('partner_read_messages', handlePartnerRead);
            socket.off('invitation_accepted', handleInvitationAccepted);
            socket.off('partner_disconnected', handleChatEnd);
            socket.off('partner_left_chat_room', handleChatEnd);
            socket.off('report_feedback', handleReportFeedback);
            socket.off('error_feedback', handleErrorFeedback);
        };
    // ★★★ 의존성 배열을 최소화하여 불필요한 재등록 방지 ★★★
    }, [socket, isReadOnlyMode, handleNewMessage, handlePartnerRead, handleInvitationAccepted, handleChatEnd, handleReportFeedback, handleErrorFeedback]);

    // Scroll to bottom effect (변경 없음)
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, [messages]);

    // 메시지 전송 로직 수정
    const handleSendMessage = () => {
        if (isReadOnlyMode) return;

        // 전송 가능 조건 확인
        const canSend = roomInfo && socket && inputMessage.trim() !== '' &&
                    roomInfo.isActive &&
                    (
                        roomInfo.type === 'REALTIME_MATCH' ||
                        roomInfo.type === 'AI_FRIEND' || // ⬅️ [추가] AI 친구는 항상 전송 가능!
                        // ▼▼▼ [수정] FRIEND 타입도 수락(isAccepted)되어야 전송 가능!
                        ((roomInfo.type === 'RECOMMEND_MATCH' || roomInfo.type === 'FRIEND') && roomInfo.isAccepted)
                    );

        if (!canSend) {
            if (roomInfo?.type === 'RECOMMEND_MATCH' && !roomInfo.isAccepted) {
                 alert('상대방이 초대를 수락해야 메시지를 보낼 수 있습니다.');
            } else if (!roomInfo?.isActive) {
                 alert('종료된 대화방입니다.');
            } else {
                 alert('메시지를 보낼 수 없는 상태입니다.');
            }
            return;
        }

        // 메시지 전송
        socket.emit('chat_message', {
            chatRoomId, message: inputMessage.trim(),
            senderId: currentUserId,
            senderNickname: currentUserNickname
        }, (response) => {
            // 서버에서 처리가 완료된 후 실행되는 부분입니다.
            if (response && response.success) {
                // 전송 성공 시에만 입력창과 로컬 스토리지 임시저장을 비웁니다!
                localStorage.removeItem(draftKey);
                setInputMessage('');
                inputRef.current?.focus();
            } else {
                // 실패 시(횟수 초과 등)에는 아무것도 하지 않습니다. 
                // 그러면 inputMessage 상태가 유지되어 입력창에 글이 그대로 남습니다.
                console.log("메시지 전송 실패 또는 차단됨");
            }
        });

        // [수정] 내가 초대를 받았고, 이것이 첫 메시지(수락)라면 서버에 알림 (Inbox 로직과 중복될 수 있으므로 서버에서 처리 권장)
        if (roomInfo.type === 'RECOMMEND_MATCH' && !roomInfo.isAccepted && roomInfo.initiator !== currentUserId) {
             socket.emit('accept_recommend_chat', { acceptorId: currentUserId, chatRoomId });
             // UI 즉시 업데이트
             setRoomInfo(prev => ({ ...prev, isAccepted: true }));
             console.log("첫 메시지 전송으로 isAccepted=true 변경됨");
        }

        // ▼▼▼ [수정] 전송 후 로컬 스토리지에서 임시저장 삭제 및 입력창 초기화 ▼▼▼
        // localStorage.removeItem(draftKey);
        // setInputMessage('');
        // inputRef.current?.focus();
    };

    // 메뉴 토글 및 신고 로직 (변경 없음)
    const handleToggleMenu = (messageId) => { setOpenMenuMessageId(prevId => (prevId === messageId ? null : messageId)); };
    const handleReportMessage = (messageToReport) => {
         setOpenMenuMessageId(null);
         const confirmed = window.confirm(`"${messageToReport.message}"\n\n이 메시지를 정말로 신고하시겠습니까?`);
         if (confirmed && socket) {
             socket.emit('report_message', {
                 messageId: messageToReport._id, chatRoomId: chatRoomId,
                 reportedUserId: messageToReport.sender, reportingUserId: currentUserId
             });
         }
    };

    // 뒤로 가기 버튼 핸들러 수정
    const handleGoBack = () => {
        // ▼▼▼ [수정] 조건문에 roomInfo?.type === 'FRIEND' 추가
        if (isReadOnlyMode || roomInfo?.type === 'RECOMMEND_MATCH' || roomInfo?.type === 'FRIEND' || roomInfo?.type === 'AI_FRIEND') {
            
            if (socket) socket.emit('leave_chat_room', { chatRoomId });
            
            // from 값이 있으면 그곳(Inbox)으로, 없으면 '/inbox'로 이동
            navigate(from || (isReadOnlyMode ? -1 : '/inbox')); 

        } else if (roomInfo?.type === 'REALTIME_MATCH') {
             // 실시간 매칭은 기존 로직 유지
             navigate(from || '/matching');
        } else {
             // 그 외의 경우에도 가급적 원래 왔던 곳(from)으로 보내줌
             navigate(from || '/'); 
        }
    };

    // [수정] 채팅 나가기 버튼 핸들러
    const handleLeaveChat = () => {
        // [수정] REALTIME_MATCH가 아니고, 읽기 전용이 아니면 수행
        if (!roomInfo || roomInfo.type === 'REALTIME_MATCH' || isReadOnlyMode) return;

        if (window.confirm('정말로 이 대화방을 종료하겠습니까? 대화 내용이 목록에서 사라집니다.')) {
            // 백엔드의 'leave_recommend_chat' 이벤트가 isActive=false로 만들어 목록에서 숨겨줍니다.
            // 친구 채팅방도 동일한 로직(목록에서 숨김)을 타면 되므로 그대로 사용합니다.
            socket.emit('leave_recommend_chat', {
              chatRoomId,
              userId: currentUserId
            });
            navigate('/inbox');
        }
    };

    const toggleFullCharacter = () => setShowFullCharacter(!showFullCharacter);

    // 로딩 중 표시
    if (loadingRoomInfo || !roomInfo) {
        return <div className="chat-page-container loading">채팅방 정보를 불러오는 중...</div>;
    }

    // 입력창 비활성화 조건 수정
    const isInputDisabled =
        isReadOnlyMode ||
        !roomInfo.isActive ||
        ((roomInfo.type === 'RECOMMEND_MATCH' || roomInfo.type === 'FRIEND') && !roomInfo.isAccepted);

    // 플레이스홀더 텍스트 수정
    const placeholderText = isInputDisabled
        ? (isReadOnlyMode ? "읽기 전용 모드입니다." :
           // ▼▼▼ [수정] FRIEND 타입 추가
           ((roomInfo.type === 'RECOMMEND_MATCH' || roomInfo.type === 'FRIEND') && !roomInfo.isAccepted
             ? (roomInfo.initiator === currentUserId ? "상대방의 수락을 기다리고 있습니다..." : "초대를 수락하고 대화를 시작하세요...")
             : "대화가 종료되었습니다."))
        : "메시지를 입력하세요...";

    return (
        // JSX 구조는 거의 동일, 헤더 부분만 약간 수정
        <div className="chat-page-container">
            {/* ... (별 배경) ... */}
            <div className="chat-header">
                <button onClick={handleGoBack} className="leave-chat-button">
                    {'<'}
                </button>
                <div className="chat-header-info">
                    {/* 1. 닉네임만 깔끔하게 출력 */}
                    <div className="nickname-row">
                        <h2>{partnerInfo ? partnerInfo.nickname : '(알 수 없음)'}</h2>
                        
                        {/* ▼▼▼ 방 타입이 AI_FRIEND가 아닐 때만(+) 버튼 렌더링 ▼▼▼ */}
                        {partnerInfo && roomInfo?.type !== 'AI_FRIEND' && (
                            <AddFriendManager 
                                partnerId={partnerInfo.userId || partnerInfo._id} 
                                defaultNickname={partnerInfo.nickname}
                            />
                        )}
                    </div>
                    
                    {/* 2. 설명글 대신 '프로필 보기' 버튼 배치 */}
                    {partnerInfo && (
                        <button className="view-profile-btn" onClick={toggleFullCharacter}>
                            프로필 보기 {'>'}
                        </button>
                    )}
                </div>
                {/* ▼▼▼ [수정] REALTIME_MATCH가 아니고 ReadOnly가 아니면 무조건 '채팅종료' 버튼 표시 ▼▼▼ */}
                {roomInfo.type !== 'REALTIME_MATCH' && !isReadOnlyMode && (
                    <button onClick={handleLeaveChat} className="leave-recommend-button">
                        채팅종료
                    </button>
                )}
                 {/* 헤더 오른쪽 공간 확보 */}
                 {(!roomInfo || roomInfo.type !== 'RECOMMEND_MATCH' || isReadOnlyMode) && <div style={{ minWidth: '60px' }}></div>}
            </div>

            <div className="chat-messages">
                {messages.map((msg, index) => {
                    // 1. 날짜 비교 (년,월,일)
                    const isSameDay = (d1, d2) => {
                        const date1 = new Date(d1);
                        const date2 = new Date(d2);
                        return date1.getFullYear() === date2.getFullYear() &&
                               date1.getMonth() === date2.getMonth() &&
                               date1.getDate() === date2.getDate();
                    };
                    
                    // 2. 시간 비교 (시,분)
                    const isSameMinute = (d1, d2) => {
                        const date1 = new Date(d1);
                        const date2 = new Date(d2);
                        return date1.getHours() === date2.getHours() &&
                               date1.getMinutes() === date2.getMinutes();
                    };

                    // --- [닉네임 로직: 과거 비교] ---
                    const prevMsg = messages[index - 1];
                    const showDateDivider = index === 0 || !isSameDay(msg.timestamp, prevMsg.timestamp);
                    const isSameSenderPrev = prevMsg && prevMsg.sender === msg.sender;
                    const isSameTimePrev = prevMsg && isSameMinute(msg.timestamp, prevMsg.timestamp);
                    
                    // 닉네임 표시: (첫 메시지) or (사람 바뀜) or (시간 바뀜) or (날짜 바뀜)
                    const showProfile = !prevMsg || !isSameSenderPrev || !isSameTimePrev || showDateDivider;

                    // --- [시간 로직: 미래 비교] ★★★ 핵심 추가 ★★★ ---
                    const nextMsg = messages[index + 1];
                    const isSameSenderNext = nextMsg && nextMsg.sender === msg.sender;
                    const isSameTimeNext = nextMsg && isSameMinute(msg.timestamp, nextMsg.timestamp);
                    
                    // 시간 표시: (마지막 메시지) or (다음 사람이 다름) or (다음 메시지와 시간이 다름)
                    // 즉, "다음에도 나랑 똑같은 시간에 보낸 메시지가 있으면" 내 시간은 숨긴다.
                    const showTime = !nextMsg || !isSameSenderNext || !isSameTimeNext;

                    const isMyMessage = msg.sender === currentUserId;

                    return (
                        <React.Fragment key={msg._id}>
                            {showDateDivider && (
                                <div className="date-divider-container">
                                    <span className='date-divider-text'>
                                        {new Date(msg.timestamp).toLocaleDateString('ko-KR', { 
                                            year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' 
                                        })}
                                    </span>
                                </div>
                            )}

                            {/* 연속 메시지면 위쪽 여백 좁히기 (continuous 클래스) */}
                            <div className={`message-row ${isMyMessage ? 'my-message-row' : 'partner-message-row'} ${!showProfile ? 'continuous' : ''}`}>
                                
                                {isMyMessage ? (
                                    /* [나의 메시지] */
                                    <div className="message-content-group">
                                        <div className="unread-container">
                                            {/* 안읽음 숫자 (1)은 계속 표시하거나 로직에 따라 숨길 수 있음 */}
                                            {unreadIds.has(msg._id) && <span className="unread-count">1</span>}
                                            
                                            {/* ★★★ [시간 표시 조건 적용] ★★★ */}
                                            {showTime && (
                                                <span className="message-timestamp">
                                                    {new Date(msg.timestamp).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </span>
                                            )}
                                        </div>
                                        <div className="my-message-bubble">
                                            {msg.message}
                                        </div>
                                    </div>
                                ) : (
                                    /* [상대방 메시지] */
                                    <div className="partner-message-group">
                                        {showProfile && (
                                            <span className="sender-nickname">
                                                {partnerInfo?.nickname || msg.senderNickname || '알 수 없음'}
                                            </span>
                                        )}
                                        
                                        <div className="partner-content-row">
                                            <div className="partner-message-bubble">
                                                {msg.message}
                                            </div>
                                            
                                            {/* ★★★ [시간 표시 조건 적용] ★★★ */}
                                            {/* 상대방 메시지는 시간이 오른쪽에 있으므로 여기서 처리 */}
                                            <div className="partner-info-col">
                                                {showTime && (
                                                    <span className="message-timestamp">
                                                        {new Date(msg.timestamp).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                    </span>
                                                )}
                                            </div>

                                            {/* 메뉴 버튼 */}
                                            {!isReadOnlyMode && roomInfo.isActive && (
                                                <div className="message-menu-container">
                                                    <button onClick={() => handleToggleMenu(msg._id)} className="message-menu-button">⋮</button>
                                                    {openMenuMessageId === msg._id && (
                                                        <div className="message-menu">
                                                            <button onClick={() => handleReportMessage(msg)}>신고하기</button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </React.Fragment>  
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            <div className="input-area">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => {
                        const text = e.target.value;
                        setInputMessage(text);
                        
                        if (socket && roomInfo?.type === 'AI_FRIEND') {
                            // 1. 글자를 다 지웠을 때: 즉시 타이핑 멈춤 신호 전송
                            if (text.length === 0) {
                                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                                // setIsTyping(false);
                                socket.emit('stopped_typing', { chatRoomId });
                                return;
                            }

                            // // 2. 글자를 치고 있을 때: 최초 1회만 타이핑 신호 전송
                            // if (!isTyping) {
                            //     setIsTyping(true);
                            //     socket.emit('typing', { chatRoomId }); // 백엔드 타이머 일시정지
                            // }

                            // // 3. 디바운싱: 2초 동안 추가 입력이 없으면 타이핑 멈춤으로 간주
                            // if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                            // typingTimeoutRef.current = setTimeout(() => {
                            //     setIsTyping(false);
                            //     socket.emit('stopped_typing', { chatRoomId }); // 백엔드 타이머 재시작
                            // }, 2000);
                            handleTypingActivity();
                        }
                    }}
                    onKeyDown={(e) => {
                        // ▼▼▼ [핵심: 모바일 호환] 글자 조합 중이든 아니든, 키가 눌리면 무조건 타이머 생명 연장 ▼▼▼
                        if (e.key !== 'Enter' && inputMessage.length > 0) {
                            handleTypingActivity();
                        }

                        if (e.nativeEvent.isComposing) return; 
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            
                            // 전송할 때는 타이핑 상태를 강제로 초기화
                            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                            isTypingRef.current = false; // [추가] 동기화
                            // setIsTyping(false); 
                            
                            handleSendMessage();
                        }
                    }}
                    placeholder={placeholderText}
                    disabled={isInputDisabled}
                />
                <button onClick={handleSendMessage} disabled={isInputDisabled}>보내기</button> {/* 버튼 disabled 조건 통일 */}
            </div>

            {showFullCharacter && partnerInfo && (
                <div className="full-character-overlay" onClick={toggleFullCharacter}>
                    <div className="full-character-popup" onClick={(e) => e.stopPropagation()}>
                        <h3>{partnerInfo.nickname}</h3>
                        <p>{partnerInfo.character}</p>
                        <button onClick={toggleFullCharacter} className="close-popup-button">닫기</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ChatPage;